/**
 * Sign-in: school picker, then credentials — the same two screens the shipped
 * app has, with the same French copy and the same "Connexion à <école>" banner.
 */
import { useState } from "react";
import { useAuth } from "@/features/auth/AuthProvider";
import { forgetSchool, selectedSchool, type School } from "@/features/auth/session";
import { messageFor } from "@/api/client";
import { SchoolSelect } from "./SchoolSelect";

export function LoginPage() {
  const { signIn } = useAuth();
  const [school, setSchool] = useState(selectedSchool());
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!school) {
    return (
      <SchoolSelect
        onPick={(picked: School) => setSchool({ id: picked.org_id, name: picked.name })}
      />
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(identifier.trim(), password);
    } catch (cause) {
      setError(messageFor(cause));
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-chrome flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-[440px] rounded-lg bg-surface px-8 py-9 shadow-2xl"
      >
        <h1 className="text-3xl font-extrabold tracking-tight text-text-strong">Dalan</h1>
        <p className="mt-1 text-sm text-text-muted">Plateforme scolaire</p>

        <div className="mt-5 rounded border border-border bg-surface-subtle px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Connexion à
          </div>
          <div className="font-bold text-text-strong">{school.name}</div>
          <button
            type="button"
            className="mt-1 text-sm text-accent underline"
            onClick={() => {
              forgetSchool();
              setSchool(null);
            }}
          >
            ← Changer d'école
          </button>
        </div>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded border-l-4 border-danger bg-danger-soft px-3 py-2 text-sm text-danger-dark"
          >
            {error}
          </p>
        )}

        <div className="mt-4">
          <label className="label" htmlFor="login-email">
            Téléphone ou e-mail
          </label>
          <input
            id="login-email"
            name="identifier"
            className="field"
            type="text"
            autoComplete="username"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            required
          />
        </div>

        <div className="mt-4">
          <label className="label" htmlFor="login-password">
            Mot de passe
          </label>
          <div className="relative">
            <input
              id="login-password"
              name="password"
              className="field pr-10"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <button
              id="login-toggle-password"
              type="button"
              className="absolute inset-y-0 right-0 px-3 text-text-muted"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
        </div>

        <button id="login-submit" type="submit" className="btn-primary mt-6 w-full" disabled={busy}>
          {busy ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </div>
  );
}
